import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, KeyRound, MonitorSmartphone, ShieldCheck } from 'lucide-react';
import { api, ApiError } from '../api/cliente.js';
import { Botao } from '../componentes/ui/Botao.js';
import { Modal } from '../componentes/ui/Modal.js';
import { Pill } from '../componentes/ui/Pill.js';

interface Preferencias {
  notificarSolicitacao: boolean;
  notificarBackup: boolean;
  resumoDiario: boolean;
}

interface PerfilDados {
  id: string;
  nome: string;
  email: string;
  tipo: 'INTERNO' | 'CLIENTE';
  tfaAtivo: boolean;
  ultimoAcesso: string | null;
  criadoEm: string;
  operadora: { id: string; nome: string } | null;
  papeis: { nome: string; descricao: string | null }[];
  permissoes: string[];
  preferencias: Preferencias;
  sessoesAtivas: number;
}

// Só o que o painel realmente dispara hoje. Cada chave bate com a preferência
// que o servidor consulta ao decidir quem recebe o e-mail.
const NOTIFICACOES: { chave: keyof Preferencias; titulo: string; detalhe: string }[] = [
  {
    chave: 'notificarSolicitacao',
    titulo: 'Solicitação de cliente',
    detalhe: 'Alteração de reverso aguardando sua aprovação.',
  },
  {
    chave: 'notificarBackup',
    titulo: 'Backup com problema',
    detalhe: 'Falha ou atraso no backup de um nó, ou no envio da cópia externa.',
  },
  {
    chave: 'resumoDiario',
    titulo: 'Resumo diário',
    detalhe: 'Um e-mail por dia com as alterações do último dia — reversos, domínios e solicitações.',
  },
];

export function Perfil() {
  const qc = useQueryClient();
  const [modal, setModal] = useState<'senha' | 'email' | '2fa' | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['perfil'],
    queryFn: () => api.get<PerfilDados>('/perfil'),
  });

  const salvarNome = useMutation({
    mutationFn: (nome: string) => api.patch('/perfil', { nome }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['perfil'] }),
  });

  const salvarPrefs = useMutation({
    mutationFn: (preferencias: Partial<Preferencias>) => api.patch('/perfil', { preferencias }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['perfil'] }),
  });

  const encerrarSessoes = useMutation({
    mutationFn: () => api.post<{ encerradas: number }>('/perfil/encerrar-outras-sessoes'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['perfil'] }),
  });

  const [nome, setNome] = useState('');
  useEffect(() => {
    if (data) setNome(data.nome);
  }, [data]);

  if (isLoading || !data) return <Esqueleto />;

  return (
    <div className="grid max-w-[980px] gap-5 lg:grid-cols-[1fr_320px]">
      <div className="space-y-5">
        {/* ------------------------------------------------------ dados */}
        <Cartao titulo="Seus dados">
          <label className="block">
            <Rotulo>Nome</Rotulo>
            <div className="flex gap-2">
              <input
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className="h-11 flex-1 rounded-controle border border-borda px-3 text-[14px] focus:border-marca focus:outline-none"
              />
              <Botao
                variante="primario"
                onClick={() => salvarNome.mutate(nome)}
                carregando={salvarNome.isPending}
                disabled={nome.trim() === data.nome || nome.trim().length < 2}
              >
                Salvar
              </Botao>
            </div>
          </label>

          <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
            <div className="min-w-0">
              <Rotulo>E-mail</Rotulo>
              <p className="truncate font-mono text-[13.5px]">{data.email}</p>
            </div>
            <Botao tamanho="tabela" onClick={() => setModal('email')}>
              Trocar e-mail
            </Botao>
          </div>
        </Cartao>

        {/* ------------------------------------------------------ notificações */}
        <Cartao
          titulo="Notificações"
          subtitulo="O que você quer receber por e-mail. Exige o envio de e-mail configurado nas Configurações."
        >
          <ul className="divide-y divide-borda-suave">
            {NOTIFICACOES.map(({ chave, titulo, detalhe }) => (
              <li key={chave} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                <input
                  type="checkbox"
                  id={chave}
                  checked={data.preferencias[chave]}
                  onChange={(e) => salvarPrefs.mutate({ [chave]: e.target.checked })}
                  className="mt-0.5 size-4 shrink-0 accent-marca"
                />
                <label htmlFor={chave} className="min-w-0 cursor-pointer">
                  <span className="block text-[13.5px] font-bold">{titulo}</span>
                  <span className="block text-[12.5px] leading-snug text-texto-suave">
                    {detalhe}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </Cartao>
      </div>

      {/* ------------------------------------------------------ lateral */}
      <div className="space-y-5">
        <Cartao titulo="Acesso">
          <div className="space-y-2.5 text-[13px]">
            <div className="flex justify-between gap-2">
              <span className="text-texto-suave">Tipo</span>
              <span className="font-bold">
                {data.tipo === 'INTERNO' ? 'Equipe Consult' : data.operadora?.nome}
              </span>
            </div>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <span className="text-texto-suave">Papéis</span>
              <span className="flex flex-wrap justify-end gap-1">
                {data.papeis.map((p) => (
                  <Pill key={p.nome} tom="info">
                    {p.nome}
                  </Pill>
                ))}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-texto-suave">Permissões</span>
              <span className="font-mono font-bold">
                {data.permissoes.includes('*') ? 'todas' : data.permissoes.length}
              </span>
            </div>
          </div>

          {/* Dito explicitamente para ninguém procurar onde editar: as
              permissões vêm do papel, e quem concede é a tela de usuários. */}
          <p className="mt-3 rounded-chip bg-borda-tenue px-3 py-2 text-[11.5px] leading-snug text-texto-suave">
            As permissões vêm dos papéis atribuídos à sua conta e não são editáveis aqui. Para
            alterá-las, use <strong>Usuários &amp; Acesso</strong>.
          </p>
        </Cartao>

        <Cartao titulo="Segurança">
          <div className="space-y-3">
            <Acao
              Icone={KeyRound}
              titulo="Senha"
              detalhe="Trocar exige a senha atual e encerra as outras sessões."
              botao="Trocar"
              aoClicar={() => setModal('senha')}
            />
            <Acao
              Icone={ShieldCheck}
              titulo="Verificação em duas etapas"
              detalhe={
                data.tfaAtivo
                  ? 'Ativa. Um código do aplicativo é pedido a cada login.'
                  : 'Desativada. Recomendada para contas com acesso total.'
              }
              botao={data.tfaAtivo ? 'Desativar' : 'Ativar'}
              aoClicar={() => setModal('2fa')}
              destaque={!data.tfaAtivo && data.permissoes.includes('*')}
            />
            <Acao
              Icone={MonitorSmartphone}
              titulo={`${data.sessoesAtivas} ${data.sessoesAtivas === 1 ? 'sessão ativa' : 'sessões ativas'}`}
              detalhe="Encerra o acesso em todos os outros aparelhos e navegadores."
              botao="Encerrar outras"
              aoClicar={() => encerrarSessoes.mutate()}
              carregando={encerrarSessoes.isPending}
              desabilitado={data.sessoesAtivas <= 1}
            />
          </div>

          {encerrarSessoes.data && (
            <p className="mt-3 flex items-center gap-1.5 text-[12.5px] font-semibold text-sucesso">
              <Check size={14} /> {encerrarSessoes.data.encerradas} sessões encerradas.
            </p>
          )}
        </Cartao>
      </div>

      {modal === 'senha' && <ModalSenha aoFechar={() => setModal(null)} />}
      {modal === 'email' && <ModalEmail aoFechar={() => setModal(null)} />}
      {modal === '2fa' && (
        <Modal2fa ativo={data.tfaAtivo} aoFechar={() => setModal(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- modais

export function ModalSenha({ aoFechar }: { aoFechar: () => void }) {
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [repetir, setRepetir] = useState('');

  const salvar = useMutation({
    mutationFn: () => api.post('/auth/trocar-senha', { senhaAtual: atual, novaSenha: nova }),
    onSuccess: () => {
      // A troca revoga todas as sessões, inclusive esta. Recarregar leva à
      // tela de login, que é o comportamento correto e esperado.
      window.location.href = '/';
    },
  });

  const divergem = repetir.length > 0 && nova !== repetir;

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo="Trocar senha"
      descricao="Você será desconectado de todos os aparelhos, inclusive deste."
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            onClick={() => salvar.mutate()}
            carregando={salvar.isPending}
            disabled={!atual || nova.length < 12 || divergem}
          >
            Trocar senha
          </Botao>
        </>
      }
    >
      <div className="space-y-4">
        <CampoSenha rotulo="Senha atual" valor={atual} aoMudar={setAtual} autoFocus />
        <CampoSenha
          rotulo="Nova senha"
          valor={nova}
          aoMudar={setNova}
          dica="Mínimo de 12 caracteres — esta conta altera o DNS de operadoras inteiras."
        />
        <CampoSenha rotulo="Repita a nova senha" valor={repetir} aoMudar={setRepetir} />
        {divergem && (
          <p className="text-[12.5px] font-semibold text-erro">As senhas não coincidem.</p>
        )}
        {salvar.error && (
          <p className="rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
            {(salvar.error as ApiError).mensagem}
          </p>
        )}
      </div>
    </Modal>
  );
}

export function ModalEmail({ aoFechar }: { aoFechar: () => void }) {
  const qc = useQueryClient();
  const [novoEmail, setNovoEmail] = useState('');
  const [senhaAtual, setSenhaAtual] = useState('');

  const salvar = useMutation({
    mutationFn: () => api.post('/perfil/email', { novoEmail, senhaAtual }),
    onSuccess: () => {
      void qc.invalidateQueries();
      aoFechar();
    },
  });

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo="Trocar e-mail"
      descricao="O e-mail é usado para entrar no painel."
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            onClick={() => salvar.mutate()}
            carregando={salvar.isPending}
            disabled={!novoEmail.includes('@') || !senhaAtual}
          >
            Trocar
          </Botao>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <Rotulo>Novo e-mail</Rotulo>
          <input
            type="email"
            value={novoEmail}
            onChange={(e) => setNovoEmail(e.target.value)}
            autoFocus
            className="h-11 w-full rounded-controle border border-borda px-3 font-mono text-[13.5px] focus:border-marca focus:outline-none"
          />
        </label>
        <CampoSenha rotulo="Sua senha atual" valor={senhaAtual} aoMudar={setSenhaAtual} />
        {salvar.error && (
          <p className="rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
            {(salvar.error as ApiError).mensagem}
          </p>
        )}
      </div>
    </Modal>
  );
}

export function Modal2fa({ ativo, aoFechar }: { ativo: boolean; aoFechar: () => void }) {
  const qc = useQueryClient();
  const [codigo, setCodigo] = useState('');
  const [senha, setSenha] = useState('');

  const iniciar = useQuery({
    queryKey: ['2fa', 'iniciar'],
    queryFn: () => api.post<{ segredo: string; qrCode: string }>('/perfil/2fa/iniciar'),
    enabled: !ativo,
    // Não repete: cada chamada gera um segredo novo, e repetir invalidaria o
    // QR Code que o usuário acabou de escanear.
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
  });

  const confirmar = useMutation({
    mutationFn: () => api.post('/perfil/2fa/confirmar', { codigo }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['perfil'] });
      aoFechar();
    },
  });

  const desativar = useMutation({
    mutationFn: () => api.post('/perfil/2fa/desativar', { senhaAtual: senha }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['perfil'] });
      aoFechar();
    },
  });

  if (ativo) {
    return (
      <Modal
        aberto
        aoFechar={aoFechar}
        titulo="Desativar verificação em duas etapas"
        descricao="Sua conta ficará protegida apenas pela senha."
        rodape={
          <>
            <Botao onClick={aoFechar}>Cancelar</Botao>
            <Botao
              variante="perigo"
              onClick={() => desativar.mutate()}
              carregando={desativar.isPending}
              disabled={!senha}
            >
              Desativar
            </Botao>
          </>
        }
      >
        <CampoSenha rotulo="Confirme com sua senha" valor={senha} aoMudar={setSenha} autoFocus />
        {desativar.error && (
          <p className="mt-4 rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
            {(desativar.error as ApiError).mensagem}
          </p>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo="Ativar verificação em duas etapas"
      descricao="Escaneie o código com o aplicativo autenticador e digite os 6 dígitos."
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            onClick={() => confirmar.mutate()}
            carregando={confirmar.isPending}
            disabled={codigo.length !== 6}
          >
            Ativar
          </Botao>
        </>
      }
    >
      <div className="space-y-4">
        {iniciar.data ? (
          <>
            <div className="flex justify-center">
              <img
                src={iniciar.data.qrCode}
                alt="Código QR para o aplicativo autenticador"
                className="size-44 rounded-chip border border-borda"
              />
            </div>
            <div>
              <Rotulo>Não consegue escanear? Digite este código</Rotulo>
              <p className="select-all break-all rounded-chip bg-fundo px-3 py-2 font-mono text-[12.5px]">
                {iniciar.data.segredo}
              </p>
            </div>
            <label className="block">
              <Rotulo>Código do aplicativo</Rotulo>
              <input
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                className="h-12 w-full rounded-controle border border-borda px-3 text-center font-mono text-[20px] tracking-[0.4em] focus:border-marca focus:outline-none"
              />
            </label>
          </>
        ) : (
          <p className="py-6 text-center text-[13px] text-texto-suave">Gerando o código…</p>
        )}
        {confirmar.error && (
          <p className="rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
            {(confirmar.error as ApiError).mensagem}
          </p>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- auxiliares

export function Cartao({
  titulo,
  subtitulo,
  children,
}: {
  titulo: string;
  subtitulo?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-card border border-borda bg-superficie p-5">
      <header className="mb-4">
        <h2 className="text-[15px] font-extrabold leading-tight">{titulo}</h2>
        {subtitulo && <p className="mt-0.5 text-[12.5px] leading-snug text-texto-suave">{subtitulo}</p>}
      </header>
      {children}
    </section>
  );
}

export function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
      {children}
    </span>
  );
}

function CampoSenha({
  rotulo,
  valor,
  aoMudar,
  dica,
  autoFocus,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  dica?: string;
  autoFocus?: boolean;
}) {
  return (
    <label className="block">
      <Rotulo>{rotulo}</Rotulo>
      <input
        type="password"
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        autoFocus={autoFocus}
        autoComplete="off"
        className="h-11 w-full rounded-controle border border-borda px-3 text-[14px] focus:border-marca focus:outline-none"
      />
      {dica && <span className="mt-1 block text-[12px] text-texto-suave">{dica}</span>}
    </label>
  );
}

export function Acao({
  Icone,
  titulo,
  detalhe,
  botao,
  aoClicar,
  carregando,
  desabilitado,
  destaque,
}: {
  Icone: typeof KeyRound;
  titulo: string;
  detalhe: string;
  botao: string;
  aoClicar: () => void;
  carregando?: boolean;
  desabilitado?: boolean;
  destaque?: boolean;
}) {
  return (
    <div
      className={`rounded-chip border p-3 ${
        destaque ? 'border-aviso/30 bg-aviso-fundo' : 'border-borda'
      }`}
    >
      <div className="flex items-start gap-2.5">
        <Icone size={16} className={`mt-0.5 shrink-0 ${destaque ? 'text-aviso' : 'text-texto-mudo'}`} />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-bold leading-tight">{titulo}</p>
          <p className="mt-0.5 text-[12px] leading-snug text-texto-suave">{detalhe}</p>
        </div>
      </div>
      <Botao
        tamanho="tabela"
        onClick={aoClicar}
        carregando={carregando}
        disabled={desabilitado}
        className="mt-2.5 w-full"
      >
        {botao}
      </Botao>
    </div>
  );
}

function Esqueleto() {
  return (
    <div className="grid max-w-[980px] gap-5 lg:grid-cols-[1fr_320px]">
      <div className="space-y-5">
        <div className="h-40 animate-pulse rounded-card border border-borda bg-superficie" />
        <div className="h-80 animate-pulse rounded-card border border-borda bg-superficie" />
      </div>
      <div className="space-y-5">
        <div className="h-52 animate-pulse rounded-card border border-borda bg-superficie" />
        <div className="h-64 animate-pulse rounded-card border border-borda bg-superficie" />
      </div>
    </div>
  );
}
